import { renderTile } from "./renderTile";
import { styles } from "../styles";
import {
  TILE_REGISTRY,
  ALL_TILE_IDS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
} from "./tileRegistry";

// 2x2 grid driven by layout.quadrants ({ topLeft, topRight, bottomLeft,
// bottomRight }). View mode renders the tile or its QuadrantView; edit
// mode (toggled from DashboardPage) replaces each slot with a picker
// card so the user can swap tiles.
//
// Per-slot resolution in view mode:
//   1. tile.QuadrantView -> render mini-view with (data, onClick, props).
//   2. else renderTile() -> standard tile / custom component (MyTime pie).
//   3. Unknown / hidden / empty id -> EmptySlot.

const SLOTS = ["topLeft", "topRight", "bottomLeft", "bottomRight"];

const SLOT_LABEL = {
  topLeft: "Top-left",
  topRight: "Top-right",
  bottomLeft: "Bottom-left",
  bottomRight: "Bottom-right",
};

function EmptySlot() {
  return (
    <div
      style={{
        border: "1px dashed #ddd",
        borderRadius: "8px",
        background: "transparent",
        minHeight: "180px",
      }}
    />
  );
}

// In edit mode, each slot turns into a picker card. The select lists
// every visible tile (so users can't pick something their caps block)
// grouped by category for easier scanning, plus an "(empty)" option.
function EditableSlot({ slot, layout, data, dashboardProps, onChange }) {
  const tileId = layout?.quadrants?.[slot] || "";
  const currentTile = tileId ? TILE_REGISTRY[tileId] : null;

  // Group available tiles by category to mirror the sidebar ordering.
  const groupedOptions = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    ids: ALL_TILE_IDS.filter((id) => {
      const t = TILE_REGISTRY[id];
      return t.category === cat && t.visible(data, dashboardProps);
    }),
  })).filter((g) => g.ids.length > 0);

  return (
    <div
      style={{
        ...styles.card,
        border: "2px dashed #1a5fb4",
        background: "#f6f8fb",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minHeight: "180px",
        padding: "14px",
      }}
    >
      <div
        style={{
          fontSize: "10px",
          fontWeight: 800,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "#1a5fb4",
        }}
      >
        {SLOT_LABEL[slot]}
      </div>
      <select
        value={tileId}
        onChange={(e) => onChange(slot, e.target.value)}
        style={{
          ...styles.input,
          margin: 0,
          padding: "8px",
          fontSize: "13px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <option value="">(empty)</option>
        {groupedOptions.map((g) => (
          <optgroup key={g.category} label={g.label}>
            {g.ids.map((id) => (
              <option key={id} value={id}>
                {TILE_REGISTRY[id].title}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div
        style={{
          fontSize: "12px",
          color: "#5a6470",
          marginTop: "auto",
        }}
      >
        {currentTile ? (
          <>
            Showing <strong>{currentTile.title}</strong>
          </>
        ) : (
          <em style={{ color: "#9aa4b0" }}>Empty slot</em>
        )}
      </div>
    </div>
  );
}

export default function DashboardQuadrant({
  layout,
  data,
  dashboardProps,
  isEditing = false,
  onChangeSlot,
}) {
  return (
    <div
      style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: "12px",
      }}
    >
      {SLOTS.map((slot) => {
        if (isEditing) {
          return (
            <EditableSlot
              key={slot}
              slot={slot}
              layout={layout}
              data={data}
              dashboardProps={dashboardProps}
              onChange={onChangeSlot}
            />
          );
        }
        const tileId = layout?.quadrants?.[slot];
        const tile = tileId ? TILE_REGISTRY[tileId] : null;
        if (!tile) return <EmptySlot key={slot} />;
        if (!tile.visible(data, dashboardProps)) {
          return <EmptySlot key={slot} />;
        }
        const onClick = dashboardProps[tile.onClickKey];
        if (tile.QuadrantView) {
          const QV = tile.QuadrantView;
          return (
            <QV
              key={slot}
              data={data}
              onClick={onClick}
              props={dashboardProps}
            />
          );
        }
        const rendered = renderTile(tileId, data, dashboardProps, slot);
        return rendered || <EmptySlot key={slot} />;
      })}
    </div>
  );
}
